<template>
  <div>
    <h1 class="mb-6 text-2xl text-gray-777 font-roboto uppercase">
      Prescrição
    </h1>
    <data-table
      v-if="loadTable"
      v-model:columns="columns"
      :route="'/patients/prescriptions/' + patient.id"
      no-data-label="Nenhuma prescrição cadastrada"
      no-result-label="Nenhuma prescrição encontrada"
    >
      <template #table.cell.content.actions="{ value, row }">
        <div class="inline-grid grid-cols-1 gap-4">
          <a
            :href="`/patients/prescriptions/download/` + row.id"
            target="_blank"
          ><font-awesome-icon
            icon="fa-eye"
            class="text-green-500 text-lg cursor-pointer"
          /></a>
        </div>
      </template>
      <template #grid.content.body.actions="{ value, row }">
        <div class="inline-grid grid-cols-1 gap-4">
          <a
            :href="`/patients/prescriptions/download/` + row.id"
            target="_blank"
          ><font-awesome-icon
            icon="fa-eye"
            class="text-green-500 text-lg cursor-pointer"
          /></a>
        </div>
      </template>

      <template #after.reload-button>
        <button
          v-if="user.role == 2"
          class="flex items-end font-roboto font-medium btn-blue uppercase"
          @click.prevent="openModalEdit({})"
        >
          <font-awesome-icon
            icon="fa-plus"
            class="text-white text-lg mr-0 lg:mr-2"
          /><span class="hidden lg:block"> Adicionar Prescrição</span>
        </button>
      </template>
    </data-table>
  </div>
  <modal-edit-steps
    ref="modalEdit"
    :steps="stepItems"
    :modal-scrollable="true"
  >
    <template #content.generate_prescription="{ item }">
      <date-time-input
        v-model="item.date"
        :error="item.errors.date"
        class="lg:pr-6 w-full lg:w-1/2"
        label="Data de emissão"
      />
      <switch-input
        v-model="item.special_control"
        class="w-full mt-6 lg:mt-0 lg:w-1/2"
        label="Receita de controle especial"
        label-checked="Sim"
        label-unchecked="Não"
      />
      <vue-select-input
        v-if="user.role == 1"
        v-model="item.professional"
        :get-option-label="(professional) => professional.user?.name"
        :options="professionals()"
        :error="item.errors[`professional.id`]"
        class="mt-6 w-full"
        label="Profissional"
        text="nenhum profissional encontrado"
      />

      <button
        class="mt-6 flex items-end font-roboto font-medium btn-blue uppercase"
        @click.prevent="item.medicines.push({ name: null })"
      >
        <font-awesome-icon
          icon="fa-plus"
          class="text-white text-lg mr-0 lg:mr-2"
        />
        Adicionar Medicamento
      </button>
      <div
        v-if="item.errors.medicines && !item.medicines.length"
        class="w-full mt-2 form-error"
      >
        {{ item.errors.medicines }}
      </div>
      <div
        v-for="(medicine, medicine_index) in item.medicines"
        class="w-full flex flex-wrap p-6 mt-4 bg-gray-100/50"
      >
        <text-input
          v-model="medicine.name"
          :error="
            item.errors[`medicines.` + medicine_index + `.name`]
          "
          class="w-5/6 lg:w-11/12"
          label="Princípio ativo / Medicamento"
        />
        <div
          class="mt-6 lg:mt-0 w-1/6 lg:w-1/12 self-center text-right"
        >
          <span @click="deleteMedicine(item, medicine)"><font-awesome-icon
            icon="fa-xmark"
            class="text-gray-600 text-lg cursor-pointer"
          /></span>
        </div>
        <text-input
          v-model="medicine.concentration"
          :error="
            item.errors[
              `medicines.` + medicine_index + `.concentration`
            ]
          "
          class="lg:pr-6 w-full mt-6 lg:w-1/3"
          label="Concentração"
        />
        <select-input
          v-model="medicine.pharmaceutical_form"
          :error="
            item.errors[
              `medicines.` +
              medicine_index +
              `.pharmaceutical_form`
            ]
          "
          class="lg:pr-6 w-full mt-6 lg:w-1/3"
          label="Forma farmacêutica"
        >
          <option :value="null" disabled class="hidden invisible">
            Forma farmacêutica
          </option>
          <option
            v-for="(
              value, name, index
            ) in prescription_pharmaceutical_forms()"
            :key="name"
            :value="name"
          >
            {{ value }}
          </option>
        </select-input>
        <select-input
          v-model="medicine.route_administration"
          :error="
            item.errors[
              `medicines.` +
              medicine_index +
              `.route_administration`
            ]
          "
          class="w-full mt-6 lg:w-1/3"
          label="Via de administração"
        >
          <option :value="null" disabled class="hidden invisible">
            Via de administração
          </option>
          <option
            v-for="(
              value, name, index
            ) in prescription_routes_administration()"
            :key="name"
            :value="name"
          >
            {{ value }}
          </option>
        </select-input>
        <text-input
          v-model="medicine.dose"
          :error="
            item.errors[`medicines.` + medicine_index + `.dose`]
          "
          class="lg:pr-6 w-full mt-6 lg:w-1/2"
          label="Dose"
        />
        <switch-input
          v-model="medicine.single_dose"
          class="lg:pr-6 w-full mt-6 lg:w-1/4"
          label="Dose única"
          label-checked="Sim"
          label-unchecked="Não"
        />
        <switch-input
          v-model="medicine.continuous_use"
          class="w-full mt-6 lg:w-1/4"
          label="Uso contínuo"
          label-checked="Sim"
          label-unchecked="Não"
        />
        <div
          v-if="medicine.continuous_use"
          class="flex flex-wrap w-full mt-6"
        >
          <div class="w-full">Posologia</div>
          <select-input
            v-if="medicine.continuous_use"
            v-model="medicine.frequency_type"
            :error="
              item.errors[
                `medicines.` +
                medicine_index +
                `.frequency_type`
              ]
            "
            class="lg:pr-6 w-full mt-6 lg:w-1/3"
            label="Frequência da dose"
          >
            <option :value="null" disabled class="hidden invisible">
              Frequência da dose
            </option>
            <option
              v-for="(
                value, name, index
              ) in prescription_frequency_types()"
              :key="name"
              :value="name"
            >
              {{ value }}
            </option>
          </select-input>
          <mask-input
            v-if="medicine.frequency_type == 'interval'"
            v-model="medicine.interval"
            mask="###"
            :error="
              item.errors[
                `medicines.` + medicine_index + `.interval`
              ]
            "
            class="w-full mt-6 lg:w-2/3"
            label="Intervalo"
            placeholder="horas"
          />
          <mask-input
            v-if="medicine.frequency_type == 'frequency'"
            v-model="medicine.frequency"
            mask="###"
            :error="
              item.errors[
                `medicines.` + medicine_index + `.frequency`
              ]
            "
            class="lg:pr-6 w-full mt-6 lg:w-2/6"
            label="Frequência"
            placeholder="vez(es)"
          />
          <select-input
            v-if="medicine.frequency_type == 'shift'"
            v-model="medicine.shift"
            :error="
              item.errors[
                `medicines.` + medicine_index + `.shift`
              ]
            "
            class="lg:pr-6 w-full mt-6 lg:w-2/6"
            label="Turno"
          >
            <option :value="null" disabled class="hidden invisible">
              Turno
            </option>
            <option
              v-for="(
                value, name, index
              ) in prescription_shifts()"
              :key="name"
              :value="name"
            >
              {{ value }}
            </option>
          </select-input>
          <text-input
            v-if="
              medicine.frequency_type == 'frequency' ||
                medicine.frequency_type == 'shift'
            "
            v-model="medicine.repeat"
            :error="
              item.errors[
                `medicines.` + medicine_index + `.repeat`
              ]
            "
            class="lg:pr-6 w-full mt-6 lg:w-1/6"
            label="A cada"
          />
          <select-input
            v-if="
              medicine.frequency_type == 'frequency' ||
                medicine.frequency_type == 'shift'
            "
            v-model="medicine.repeat_type"
            :error="
              item.errors[
                `medicines.` + medicine_index + `.repeat_type`
              ]
            "
            class="w-full mt-6 lg:w-1/6"
            label="Período"
          >
            <option :value="null" disabled class="hidden invisible">
              Período
            </option>
            <option
              v-for="(
                value, name, index
              ) in prescription_frequencies()"
              :key="name"
              :value="name"
            >
              {{ value }}
            </option>
          </select-input>
        </div>
        <div
          v-if="medicine.continuous_use"
          class="flex flex-wrap w-full mt-6"
        >
          <div class="w-full">Período de tratamento</div>
          <date-input
            v-model="medicine.date_start"
            :error="
              item.errors[
                `medicines.` + medicine_index + `.date_start`
              ]
            "
            class="mt-6 lg:pr-6 w-full lg:w-1/2"
            label="Data de início"
          />
          <mask-input
            v-model="medicine.duration"
            mask="###"
            :error="
              item.errors[
                `medicines.` + medicine_index + `.duration`
              ]
            "
            class="lg:pr-6 w-full mt-6 lg:w-1/4"
            label="Duração"
          />
          <select-input
            v-model="medicine.duration_type"
            :error="
              item.errors[
                `medicines.` + medicine_index + `.duration_type`
              ]
            "
            class="w-full mt-6 lg:w-1/4"
            label="Período"
          >
            <option :value="null" disabled class="hidden invisible">
              Período
            </option>
            <option
              v-for="(
                value, name, index
              ) in prescription_frequencies()"
              :key="name"
              :value="name"
            >
              {{ value }}
            </option>
          </select-input>
        </div>
      </div>
      <textarea-input
        v-model="item.description"
        maxlength="1000"
        :length-counter="true"
        :error="item.errors.description"
        class="mt-6 w-full"
        label="Observações"
      />
    </template>
    <template #content.download_prescription="{ item }">
      <div class="w-full">
        Faça o download da prescrição e assine o documento digitalmente:
      </div>
      <div class="w-full flex justify-center mt-5">
        <button
          type="button"
          class="px-6 py-2.5 bg-blue-600 text-white font-roboto font-medium text-xs leading-tight uppercase rounded shadow-md ml-1 hover:bg-blue-700 hover:shadow-lg focus:bg-blue-700 focus:shadow-lg focus:outline-none focus:ring-0 active:bg-blue-800 active:shadow-lg transition duration-150 ease-in-out"
          @click.prevent="download(item)"
        >
          Download
        </button>
      </div>
    </template>
    <template #content.upload_prescription="{ item }">
      <div class="w-full">
        Faça o upload do documento assinado digitalmente:
      </div>
      <file-input
        v-model="item.file"
        :error="item.errors.file"
        class="mt-6 w-full"
        type="file"
        accept="application/pdf"
      />
    </template>
    <template #content.success>
      <div class="w-full text-center">
        <font-awesome-icon
          icon="fa-regular fa-circle-check"
          class="text-green-600 text-7xl mr-0 lg:mr-2"
        />
      </div>
      <div class="w-full text-center mt-5 uppercase">
        O documento foi enviado para o paciente
      </div>
    </template>
  </modal-edit-steps>
</template>

<script>
import { computed } from 'vue'
import { usePage } from '@inertiajs/inertia-vue3'
import format from 'date-fns/format'
import LoadingButton from '@/Shared/LoadingButton'
import ModalEdit from '@/Shared/ModalEdit'
import ModalEditSteps from '@/Shared/ModalEditSteps'
import DataTable from '@/Shared/DataTable'
import SwitchInput from '@/Shared/SwitchInput'
import TextInput from '@/Shared/TextInput'
import VueSelectInput from '@/Shared/VueSelectInput'
import DateTimeInput from '@/Shared/DateTimeInput'
import TextareaInput from '@/Shared/TextareaInput'
import FileInput from '@/Shared/FileInput'
import SelectInput from '@/Shared/SelectInput'
import MaskInput from '@/Shared/MaskInput'
import DateInput from '@/Shared/DateInput'

export default {
  components: {
    LoadingButton,
    ModalEdit,
    ModalEditSteps,
    DataTable,
    SwitchInput,
    TextInput,
    VueSelectInput,
    DateTimeInput,
    TextareaInput,
    FileInput,
    SelectInput,
    MaskInput,
    DateInput,
  },
  inject: [
    'patient',
    'professionals',
    'prescription_pharmaceutical_forms',
    'prescription_routes_administration',
    'prescription_frequencies',
    'prescription_frequency_types',
    'prescription_shifts',
  ],
  setup() {
    const user = computed(() => usePage().props.value.auth.user)
    return { user }
  },
  data() {
    return {
      loadTable: true,
      stepItems: [
        {
          idStep: 'generate_prescription',
          title: 'Gerar prescrição',
          submitCallback: (item, callback) => {
            let success = false
            item.post('/patients/prescriptions/validate-step-one', {
              forceFormData: true,
              onFinish: () => {
                let success = false
                if (!Object.keys(item.errors).length) {
                  success = true
                }
                callback(success)
              },
            })
            return success
          },
        },
        {
          idStep: 'download_prescription',
          title: 'Download da prescrição',
        },
        {
          idStep: 'upload_prescription',
          title: 'Envie o documento assinado digitalmente',
          submitCallback: (item, callback) => {
            let success = false
            item.post(
              '/patients/prescriptions/' + this.patient.id,
              {
                forceFormData: true,
                onFinish: () => {
                  let success = false
                  if (!Object.keys(item.errors).length) {
                    success = true
                    this.reloadTable()
                  }
                  callback(success)
                },
              },
            )
            return success
          },
        },
        {
          idStep: 'success',
          title: 'Prescrição enviada para o paciente',
        },
      ],
      columns: [
        {
          uniqid: 'prescription_date',
          label: 'Data de Emissão',
          field: 'date',
          visible: true,
          sortable: true,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm',
          headerClass: 'p-4 text-left',
          format: (val, row) =>
            format(new Date(row.date), 'dd/MM/yyy HH:mm'),
        },
        {
          uniqid: 'prescription_description',
          label: 'Prescrição',
          field: 'description',
          visible: true,
          sortable: true,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm overflow-hidden',
          headerClass: 'p-4 text-left',
          format: (val, row) => (val ? val.substr(0, 50) : ''),
        },
        {
          uniqid: 'prescription_special_control',
          label: 'Controle Especial',
          field: 'special_control',
          visible: true,
          sortable: true,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm',
          headerClass: 'p-4 text-left',
          format: (val, row) => (val ? 'Sim' : 'Não'),
        },
        {
          uniqid: 'prescription_professional',
          label: 'Profissional',
          field: 'professional_user.name',
          visible: true,
          sortable: true,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm',
          headerClass: 'p-4 text-left',
          format: (val, row) =>
            row.id ? row.professional.user.name : '',
        },
        {
          uniqid: 'actions',
          label: 'Ações',
          field: 'id',
          visible: true,
          align: 'center',
          classes: 'py-3 border-b',
          headerClass: 'p-4',
        },
      ],
    }
  },
  methods: {
    openModalEdit(item) {
      this.$refs.modalEdit.openModalEdit({
        id: item.id,
        description: item.description,
        date: item.date,
        file: item.file,
        medicines: item.medicines ?? [],
        professional: item.professional ?? null,
        special_control: item.special_control ?? false,
      })
    },
    reloadTable() {
      this.loadTable = false
      setTimeout(() => (this.loadTable = true), 10)
    },
    download(item) {
      let data = {
        _token: this.$page.props.csrf_token,
        type_document: 'prescription',
        date: item.date,
        description: item.description,
        medicines: item.medicines,
        special_control: item.special_control ? '1' : '',
        professional_id: item.professional?.id,
        patient_id: this.patient.id,
      }
      let url = '/patients/pdf/generate'
      this.openWindowWithPost(url, data)
    },
    openWindowWithPost(url, data) {
      // console.log(document.querySelector('meta[name="csrf-token"]').getAttribute('content'))

      var form = document.createElement('form')
      form.target = '_blank'
      form.method = 'POST'
      form.action = url
      form.style.display = 'none'

      // const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      // const csrfInput = document.createElement('input')
      // csrfInput.type = 'hidden'
      // csrfInput.name = '_token'
      // csrfInput.value = csrfToken
      // form.appendChild(csrfInput)

      for (var key in data) {
        this.generateInput(form, key, data[key])
      }

      document.body.appendChild(form)
      form.submit()
      document.body.removeChild(form)
    },
    generateInput(form, key, value) {
      if (typeof value == 'object') {
        for (const [obj_key, obj_value] of Object.entries(value)) {
          let k = key + '|' + obj_key
          this.generateInput(form, k, obj_value)
        }
      } else {
        let k = key.split('|')
        if (k.length > 1) {
          for (let i in k) {
            if (i == 0) {
              key = k[i]
            } else {
              key += '[' + k[i] + ']'
            }
          }
        }
        var input = document.createElement('input')
        input.type = 'hidden'
        input.name = key
        input.value = typeof value != 'undefined' ? value : ''
        form.appendChild(input)
      }
    },
    deleteMedicine(form, medicine) {
      form.medicines.splice(form.medicines.indexOf(medicine), 1)
    },
  },
}
</script>
